// Domain Manage panel — Loot → Products → a domain row → "Manage".
//
// Lets staff change a shopper's DNS records, nameservers, auto-renew,
// forwarding and send the EPP code without impersonating them (impersonation
// denies every domain write). Every change goes preview → Apply: the first call
// asks the tool what it WOULD do and shows its own sentence; only Apply sends
// confirm:true. The backend records each applied change against the signed-in
// staffer in the account's Events tab.

import { useCallback, useEffect, useState } from 'react';
import { domainAction, getDomainDetail } from '../api';
import { DNS_TYPES, readOutcome, parseNameservers, validateRecord, validateForwardTarget } from '../lib/domainManage';

const btn = { padding: '4px 10px', background: 'transparent', color: 'var(--text2)', border: '1px solid var(--border)', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--mono)' };
const primary = { ...btn, background: 'var(--accent)', color: '#fff', border: 'none' };
const input = { padding: '5px 7px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)', fontSize: 12, fontFamily: 'var(--mono)', outline: 'none', minWidth: 0 };
const label = { fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text3)', fontFamily: 'var(--mono)', marginBottom: 6 };
const section = { padding: '12px 0', borderTop: '1px solid var(--border)' };

export default function DomainManagePanel({ accountId, domain }) {
  const [records, setRecords] = useState({ loading: true, list: [], error: '' });
  const [detail, setDetail] = useState(null);
  const [pending, setPending] = useState(null);   // { name, args, message, nsRequired, title }
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);     // { ok, text }

  const loadRecords = useCallback(async () => {
    setRecords(r => ({ ...r, loading: true, error: '' }));
    try {
      const out = readOutcome(await domainAction(accountId, 'get_dns_records', { domain }));
      if (!out.ok) setRecords({ loading: false, list: [], error: out.message });
      else setRecords({ loading: false, list: out.result?.records || [], error: '' });
    } catch (e) {
      setRecords({ loading: false, list: [], error: e.message });
    }
  }, [accountId, domain]);

  const loadDetail = useCallback(async () => {
    try { setDetail(await getDomainDetail(domain)); } catch { setDetail(null); }
  }, [domain]);

  useEffect(() => { loadRecords(); loadDetail(); }, [loadRecords, loadDetail]);

  // Step 1: ask the tool what it would do.
  async function preview(name, args, title) {
    setBusy(true); setNotice(null); setPending(null); setAck(false);
    try {
      const out = readOutcome(await domainAction(accountId, name, { ...args, domain, confirm: false }, { confirmed: true }));
      if (!out.ok) setNotice({ ok: false, text: out.message });
      else if (out.preview) setPending({ name, args, title, message: out.message, nsRequired: out.nameserverChangeRequired });
      else setNotice({ ok: true, text: out.message || 'Nothing to change.' });
    } catch (e) {
      setNotice({ ok: false, text: e.message });
    } finally { setBusy(false); }
  }

  // Step 2: apply exactly what was previewed.
  async function apply() {
    if (!pending) return;
    setBusy(true);
    try {
      const args = { ...pending.args, domain, confirm: true };
      if (pending.nsRequired) args.acknowledgeNameserverChange = true;
      const out = readOutcome(await domainAction(accountId, pending.name, args, { confirmed: true }));
      setNotice(out.ok
        ? { ok: true, text: `${pending.title} — done. Logged to this account's Events.` }
        : { ok: false, text: out.message });
      setPending(null);
      if (out.ok) { loadRecords(); loadDetail(); }
    } catch (e) {
      setNotice({ ok: false, text: e.message });
    } finally { setBusy(false); }
  }

  const reg = detail?.registrar?.data || {};
  const autoRenewOn = reg.autoRenew === true;

  return (
    <div style={{ padding: '4px 0 12px', fontSize: 12 }}>
      {pending && (
        <div role="alertdialog" aria-label={`Confirm ${pending.title}`} style={{ padding: '10px 12px', marginBottom: 10, borderRadius: 6, background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.3)' }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{pending.title}</div>
          <div style={{ color: 'var(--text2)', whiteSpace: 'pre-wrap' }}>{pending.message || 'Apply this change?'}</div>
          {pending.nsRequired && (
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8, color: '#fbbf24' }}>
              <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} />
              The customer agreed to move all DNS for {domain} to Squish.
            </label>
          )}
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button data-write style={primary} disabled={busy || (pending.nsRequired && !ack)} onClick={apply}>{busy ? 'Applying…' : 'Apply'}</button>
            <button style={btn} disabled={busy} onClick={() => setPending(null)}>Cancel</button>
          </div>
        </div>
      )}
      {notice && (
        <div style={{ marginBottom: 10, fontFamily: 'var(--mono)', fontSize: 11, color: notice.ok ? '#4ade80' : '#f87171' }}>
          {notice.ok ? '✓ ' : '⚠ '}{notice.text}
        </div>
      )}

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', paddingBottom: 12 }}>
        <div>
          <div style={label}>Auto-renew</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontFamily: 'var(--mono)' }}>{detail ? (autoRenewOn ? 'On' : 'Off') : '—'}</span>
            <button data-write style={btn} disabled={busy || !detail}
              onClick={() => preview('toggle_autorenew', { enabled: !autoRenewOn }, autoRenewOn ? 'Turn auto-renew off' : 'Turn auto-renew on')}>
              {autoRenewOn ? 'Turn off' : 'Turn on'}
            </button>
          </div>
        </div>
        <div>
          <div style={label}>Transfer out</div>
          <button data-write style={btn} disabled={busy}
            onClick={() => preview('request_auth_code', {}, 'Email the EPP code to the registrant')}>
            Email EPP code
          </button>
        </div>
      </div>

      <NameserversSection current={reg.nameservers || []} busy={busy}
        onSave={(nameservers) => preview('update_nameservers', { nameservers }, 'Change nameservers')} />

      <ForwardingSection busy={busy}
        onSave={(target) => preview('setup_domain_forwarding', { target }, `Forward ${domain}`)} />

      <div style={section}>
        <div style={{ ...label, display: 'flex', justifyContent: 'space-between' }}>
          <span>DNS records</span>
          <button style={{ ...btn, padding: '1px 8px' }} onClick={loadRecords} disabled={records.loading}>↻</button>
        </div>
        {records.error && <div style={{ color: '#f87171', fontSize: 11, marginBottom: 6 }}>⚠ {records.error}</div>}
        {records.loading ? (
          <div style={{ color: 'var(--text3)' }}>Loading records…</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {records.list.length === 0 && !records.error && (
              <div style={{ color: 'var(--text3)' }}>No records — the domain may use external nameservers.</div>
            )}
            {records.list.map(r => (
              <RecordRow key={r.recordId || `${r.type}:${r.host}:${r.value}`} record={r} busy={busy}
                onUpdate={(changes) => preview('update_dns_record', { recordId: String(r.recordId), ...changes }, `Update ${r.type} ${r.host || '@'}`)}
                onDelete={() => preview('delete_dns_record', { recordId: String(r.recordId) }, `Delete ${r.type} ${r.host || '@'}`)} />
            ))}
            <AddRecordRow busy={busy}
              onAdd={(rec) => preview('create_dns_record', rec, `Add ${rec.type} ${rec.host || '@'}`)} />
          </div>
        )}
      </div>
    </div>
  );
}

function NameserversSection({ current, busy, onSave }) {
  const [text, setText] = useState('');
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  return (
    <div style={section}>
      <div style={label}>Nameservers</div>
      {!editing ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'var(--mono)', color: 'var(--text2)' }}>{current.length ? current.join(' · ') : '—'}</span>
          <button data-write style={btn} disabled={busy} onClick={() => { setText(current.join('\n')); setError(''); setEditing(true); }}>Change</button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <textarea rows={3} value={text} onChange={e => setText(e.target.value)} style={{ ...input, width: 280 }} aria-label="Nameservers, one per line" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <button data-write style={primary} disabled={busy} onClick={() => {
              const { nameservers, error: err } = parseNameservers(text);
              setError(err);
              if (!err) { onSave(nameservers); setEditing(false); }
            }}>Preview</button>
            <button style={btn} onClick={() => setEditing(false)}>Cancel</button>
          </div>
          {error && <div style={{ color: '#f87171', fontSize: 11, width: '100%' }}>{error}</div>}
        </div>
      )}
    </div>
  );
}

function ForwardingSection({ busy, onSave }) {
  const [target, setTarget] = useState('');
  const [error, setError] = useState('');
  return (
    <div style={section}>
      <div style={label}>Forwarding</div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <input value={target} onChange={e => setTarget(e.target.value)} placeholder="https://example.com" style={{ ...input, width: 280 }} aria-label="Forward to URL" />
        <button data-write style={btn} disabled={busy || !target.trim()} onClick={() => {
          const err = validateForwardTarget(target);
          setError(err);
          if (!err) onSave(target.trim());
        }}>Preview</button>
      </div>
      {error && <div style={{ color: '#f87171', fontSize: 11, marginTop: 4 }}>{error}</div>}
    </div>
  );
}

const grid = { display: 'grid', gridTemplateColumns: '64px minmax(80px,1fr) minmax(120px,3fr) 64px auto', gap: 6, alignItems: 'center' };

function RecordRow({ record, busy, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(record.value || '');
  const [ttl, setTtl] = useState(record.ttl ?? '');
  const [error, setError] = useState('');
  const canWrite = record.recordId != null;
  return (
    <div>
      <div style={grid}>
        <span style={{ fontFamily: 'var(--mono)', fontWeight: 700 }}>{record.type}</span>
        <span style={{ fontFamily: 'var(--mono)', color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{record.host || '@'}</span>
        {editing
          ? <input value={value} onChange={e => setValue(e.target.value)} style={input} aria-label="Record value" />
          : <span style={{ fontFamily: 'var(--mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={record.value}>{record.value}</span>}
        {editing
          ? <input value={ttl} onChange={e => setTtl(e.target.value)} style={input} aria-label="TTL" />
          : <span style={{ fontFamily: 'var(--mono)', color: 'var(--text3)' }}>{record.ttl ?? ''}</span>}
        <span style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
          {editing ? (
            <>
              <button data-write style={primary} disabled={busy} onClick={() => {
                const err = validateRecord({ type: record.type, value, ttl });
                setError(err);
                if (err) return;
                const changes = { value: value.trim() };
                if (ttl !== '' && Number(ttl) !== record.ttl) changes.ttl = Number(ttl);
                onUpdate(changes); setEditing(false);
              }}>Preview</button>
              <button style={btn} onClick={() => { setEditing(false); setValue(record.value || ''); setTtl(record.ttl ?? ''); setError(''); }}>✕</button>
            </>
          ) : canWrite && (
            <>
              <button data-write style={btn} disabled={busy} onClick={() => setEditing(true)}>Edit</button>
              <button data-write style={btn} disabled={busy} onClick={onDelete}>Delete</button>
            </>
          )}
        </span>
      </div>
      {error && <div style={{ color: '#f87171', fontSize: 11 }}>{error}</div>}
    </div>
  );
}

function AddRecordRow({ busy, onAdd }) {
  const [type, setType] = useState('A');
  const [host, setHost] = useState('');
  const [value, setValue] = useState('');
  const [ttl, setTtl] = useState('3600');
  const [error, setError] = useState('');
  return (
    <div style={{ marginTop: 6 }}>
      <div style={grid}>
        <select value={type} onChange={e => setType(e.target.value)} style={input} aria-label="Record type">
          {DNS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input value={host} onChange={e => setHost(e.target.value)} placeholder="@ or www" style={input} aria-label="Host" />
        <input value={value} onChange={e => setValue(e.target.value)} placeholder="Value" style={input} aria-label="Value" />
        <input value={ttl} onChange={e => setTtl(e.target.value)} style={input} aria-label="TTL" />
        <button data-write style={btn} disabled={busy || !value.trim()} onClick={() => {
          const err = validateRecord({ type, value, ttl });
          setError(err);
          if (err) return;
          const rec = { type, host: host.trim() === '@' ? '' : host.trim(), value: value.trim() };
          if (ttl !== '') rec.ttl = Number(ttl);
          onAdd(rec);
        }}>+ Add</button>
      </div>
      {error && <div style={{ color: '#f87171', fontSize: 11 }}>{error}</div>}
    </div>
  );
}
